import { getFullPath, splitPathIntoSegments } from '../../../generic/FileTree/buildTreeUtils';
import type { VendoredDependencyPath } from '../../../../../shared/vendoredDependencies';

export type VendoredDependencyTreeNode = {
  type: 'directory' | 'file';
  name: string;
  path: string;
  locator?: string;
  children?: VendoredDependencyTreeNode[];
};

//  Intermediate type while building the tree. The Map allows us to do a flat lookup for inserting new nodes.
type TreeBuilderNode = {
  path: string;
  locator?: string;
  children: Map<string, TreeBuilderNode>;
};

/**
 * Get a fresh node
 * @param {string} path - A given path segment
 * @param {string} locator - Optional locator for the dependency
 */
const getNewNode = (path: string, locator?: string): TreeBuilderNode => ({
  path,
  locator,
  children: new Map(),
});

/**
 * Insert a path in the tree.
 * Doesn't return anything because it is setting the various children Maps
 * @param {TreeBuilderNode} root - The root node to insert children into.
 * @param {VendoredDependencyPath} item - A vendored dependency path item.
 */
function insertPath(root: TreeBuilderNode, item: VendoredDependencyPath): void {
  let currentNode = root;
  const segments = splitPathIntoSegments(item.path);

  //  Iterate each segment, injecting the children at the correct node
  segments.forEach((segment, index) => {
    let currentSegment = currentNode.children.get(segment);
    //  If the current node doesn't have the segment, set it up
    if (!currentSegment) {
      //  Only set locator on the final segment (leaf node)
      const isLeaf = index === segments.length - 1;
      currentSegment = getNewNode(segment, isLeaf ? item.locator : undefined);
      currentNode.children.set(segment, currentSegment);
    }
    //  "Step down" the tree now
    currentNode = currentSegment;
  });
}

/**
 * Once we have built the tree, we want to collapse the children down to VendoredDependencyTreeNode.
 * We need to know the full file path for the `path` on VendoredDependencyTreeNode (needs to be unique across the tree).
 * @param {TreeBuilderNode} node - A TreeBuilderNode element
 * @param {string} parentPath - The full path to the parent node.
 * @param {boolean} [shouldIncludeTrailingSlash] - If directories should include a trailing slash.
 */
function collapse(
  node: TreeBuilderNode,
  parentPath = '',
  shouldIncludeTrailingSlash = true,
): VendoredDependencyTreeNode {
  //  Get the full file path so we can use it as a unique ID for nodes
  const fullPath = getFullPath(node.path, parentPath);
  const isRoot = fullPath === '/';
  //  Root node is already a dir, we don't need to add an additional trailing slash.
  const extraTrailingSlash = shouldIncludeTrailingSlash && !isRoot ? '/' : '';

  //  Handle leaves first, since they don't have any children nodes anyway.
  const isLeaf = !node.children.size; // No children, you're a leaf.
  if (isLeaf && !isRoot) {
    return {
      type: 'file',
      name: node.path,
      path: fullPath,
      locator: node.locator,
    };
  }

  //  Recursively collapse all of the inner children.
  const children: VendoredDependencyTreeNode[] = Array.from(node.children.values())
    .map(child => collapse(child, fullPath));

  return {
    type: 'directory',
    name: `${node.path}${extraTrailingSlash}`,
    path: `${fullPath}${extraTrailingSlash}`,
    locator: node.locator,
    children,
  };
}

/**
 * Takes a flat list of VendoredDependencyPath and builds a tree structure suitable
 * for passing to React Arborist.
 *
 * @param {VendoredDependencyPath[]} paths - List of vendored dependency paths.
 * @param {boolean} [shouldIncludeTrailingSlash] - If directories should include a trailing slash.
 */
export default function buildTree(
  paths: VendoredDependencyPath[],
  shouldIncludeTrailingSlash = true,
): VendoredDependencyTreeNode[] {
  //  Root starts at "/"
  const root = getNewNode('/', undefined);

  paths.forEach(item => {
    insertPath(root, item);
  });

  //  Recursively collapse all children nodes.
  //  We wrap this in an array because react-arborist expects a list of top-level nodes (in our case just one root).
  return [collapse(root, '', shouldIncludeTrailingSlash)];
}
